import { Button } from "../../primitives/button";
import { RefreshCw, Monitor, Loader2 } from "lucide-react";

interface PreviewPanelProps {
  // One-time exchange URL used to bootstrap the preview auth flow on first load.
  // The iframe navigates here once; the container sets an HttpOnly cookie and
  // redirects to the session root. After that, the browser's cookies carry auth
  // and the iframe navigates freely within the preview domain.
  // Production path: minted by the API via PREVIEW_JWT_PRIVATE_KEY.
  exchangeUrl: string | null;
  // Direct container URL used as a fallback when the JWT exchange infrastructure
  // is not available (local dev with SANDBOX_BACKEND=docker). Never set in
  // production — the exchange URL is always available there.
  directUrl?: string | null;
  healthy: boolean;
  onRefresh: () => void;
}

export function PreviewPanel({
  exchangeUrl,
  directUrl,
  healthy,
  onRefresh,
}: PreviewPanelProps) {
  // Prefer the JWT-gated exchange URL (production); fall back to the direct
  // container URL for local dev where JWT env vars are not configured.
  const previewUrl = exchangeUrl ?? directUrl ?? null;

  if (!previewUrl) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-muted-foreground gap-2">
        <Monitor className="size-8 opacity-40" />
        <p className="text-sm">Start a session to see the live preview</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Toolbar */}
      <div className="flex items-center justify-between border-b border-border px-3 py-1.5 bg-muted/30">
        <div className="flex items-center gap-2 text-xs text-muted-foreground min-w-0">
          <span
            className={`inline-block size-2 rounded-full flex-shrink-0 ${
              healthy ? "bg-green-500" : "bg-yellow-500"
            }`}
          />
          <span className="truncate">
            {healthy ? "Preview ready" : "Starting up..."}
          </span>
        </div>
        <Button
          variant="ghost"
          size="icon"
          className="size-7"
          onClick={onRefresh}
        >
          <RefreshCw className="size-3.5" />
        </Button>
      </div>

      {/* Preview content */}
      {!healthy ? (
        <div className="flex flex-col items-center justify-center flex-1 text-muted-foreground gap-2">
          <Loader2 className="size-6 animate-spin" />
          <p className="text-sm">Preview is starting up...</p>
        </div>
      ) : (
        <iframe
          src={previewUrl}
          className="flex-1 w-full border-0"
          title="Live Preview"
          sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
        />
      )}
    </div>
  );
}
